/**
 * Sales Call Examples - XML-structured borderline calibration
 * Phase 2.2: Examples for accurate scoring calibration
 */

export const SALES_CALL_EXAMPLES = `
<examples category="sales-call">

  <!-- Objection Handling: Developing (67) vs Proficient (83) -->

  <example category="objection_handling" score="67" tier="developing">
    <dialogue>
      <customer speaker="customer" timestamp="05:23">That's expensive</customer>
      <agent speaker="agent" timestamp="05:26">
        That's fair. We have a smaller package at $X. Would that work?
      </agent>
    </dialogue>
    <analysis>
      <component name="recognition">1 (acknowledged)</component>
      <component name="empathy">1 (validated "fair")</component>
      <component name="clarification">0 (no probing question)</component>
      <component name="response">1 (offered alternative)</component>
      <component name="confirmation">1 (asked "would that work")</component>
      <calculation>(4/6) × 100 = 67</calculation>
    </analysis>
    <improvement>
      Missing clarification question to understand budget context.
      Better: "I understand. What were you expecting to invest?"
    </improvement>
  </example>

  <example category="objection_handling" score="83" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="05:23">That's expensive</customer>
      <agent speaker="agent" timestamp="05:26">
        I understand. What were you budgeting for? Because many clients
        find the $Y savings offset the cost within 60 days.
      </agent>
    </dialogue>
    <analysis>
      <component name="recognition">1</component>
      <component name="empathy">1 ("I understand")</component>
      <component name="clarification">1 (asked budget)</component>
      <component name="response">2 (ROI value reframe)</component>
      <component name="confirmation">0 (no explicit check that the concern was resolved)</component>
      <calculation>(1+1+1+2+0)/6 × 100 = 83</calculation>
    </analysis>
    <threshold_note>
      Score 70+ means at least 5 of 6 points; here only the explicit confirmation is missing.
      This example demonstrates proficient objection handling.
    </threshold_note>
  </example>

  <borderline_calibration score_A="67" score_B="83">
    <question>What separates Developing (67) from Proficient (83)?</question>
    <answer>
      - 70+ is at least 5 of 6 points, 67 is 4 of 6
      - 67 can have alternative offered but lacks deeper understanding
      - 70+ shows customer-centric approach (understand first, then respond)
      - 67 is reactive (immediate solution), 70+ is consultative (explore before solving)
    </answer>
  </borderline_calibration>

  <!-- Needs Qualification (BANT): Critical (13), Developing (50), Proficient (75) -->

  <example category="needs_qualification_bant" score="13" tier="critical">
    <dialogue>
      <agent speaker="agent" timestamp="01:15">So what brings you in today?</agent>
      <customer speaker="customer" timestamp="01:18">We need better reporting</customer>
      <agent speaker="agent" timestamp="01:20">
        Great! Our Pro plan has advanced reports. Want to see a demo?
      </agent>
    </dialogue>
    <analysis>
      <bant>
        <budget>0 (not asked)</budget>
        <authority>0 (unknown)</authority>
        <need>1 (stated but not quantified)</need>
        <timeline>0 (none)</timeline>
      </bant>
      <calculation>(0+0+1+0)/8 × 100 = 12.5, rounded to 13</calculation>
    </analysis>
    <improvement>
      Missing 3 out of 4 BANT elements. Should ask:
      - Budget: "What's your budget for reporting tools?"
      - Authority: "Who else is involved in this decision?"
      - Timeline: "When do you need this in place?"
    </improvement>
  </example>

  <example category="needs_qualification_bant" score="75" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="01:15">What challenges are you facing with reporting?</agent>
      <customer speaker="customer" timestamp="01:20">We're losing 10 hours/week on manual reports</customer>
      <agent speaker="agent" timestamp="01:25">That's significant. What's your budget for solving this?</agent>
      <customer speaker="customer" timestamp="01:30">We have $50K allocated</customer>
      <agent speaker="agent" timestamp="01:35">Perfect. When do you need this resolved?</agent>
      <customer speaker="customer" timestamp="01:38">Sometime next year, there is no hard deadline</customer>
      <agent speaker="agent" timestamp="01:42">Any driver, like a budget cycle?</agent>
      <customer speaker="customer" timestamp="01:46">Not really, maybe early in the year</customer>
      <agent speaker="agent" timestamp="01:50">Are you the final decision maker, or is there a team involved?</agent>
      <customer speaker="customer" timestamp="01:53">I need approval from CFO</customer>
    </dialogue>
    <analysis>
      <bant>
        <budget>2 (specific amount: $50K)</budget>
        <authority>1 (identified: CFO approval needed)</authority>
        <need>2 (quantified: 10h/week loss)</need>
        <timeline>1 (still vague when probed)</timeline>
      </bant>
      <calculation>(2+1+2+1)/8 × 100 = 75</calculation>
    </analysis>
    <threshold_note>
      Score 70+ means at least 6 of 8 points (5 of 8 is 63).
      This demonstrates methodical qualification process.
    </threshold_note>
  </example>

  <example category="needs_qualification_bant" score="50" tier="developing">
    <dialogue>
      <agent speaker="agent" timestamp="01:15">Tell me about your reporting needs</agent>
      <customer speaker="customer" timestamp="01:20">We lose about 8 hours a week on manual reports</customer>
      <agent speaker="agent" timestamp="01:25">When do you want to have this fixed?</agent>
      <customer speaker="customer" timestamp="01:28">Soon, in the coming months</customer>
      <agent speaker="agent" timestamp="01:32">What budget do you have in mind?</agent>
      <customer speaker="customer" timestamp="01:35">Probably not huge, but I need to check</customer>
    </dialogue>
    <analysis>
      <bant>
        <budget>1 (mentioned, no figure or range)</budget>
        <authority>0 (not asked)</authority>
        <need>2 (quantified: 8h/week loss)</need>
        <timeline>1 (vague: 'in the coming months')</timeline>
      </bant>
      <calculation>(1+0+2+1)/8 × 100 = 50</calculation>
    </analysis>
  </example>

  <borderline_calibration score_A="50" score_B="75">
    <question>What separates Developing BANT (50) from Proficient (75)?</question>
    <answer>
      - 75 is 6 of 8 points, 50 is 4 of 8
      - 50 typically has 2-3 elements (e.g., need + timeline + a budget hint, but no authority)
      - 75+ shows methodical qualification, 50 shows partial discovery
      - 75+ gets specific answers (amounts, roles), 50 accepts vague responses
      - 75+ probes deeper on vague answers, 50 moves on without clarification
    </answer>
  </borderline_calibration>

  <!-- Closing Technique: Weak (30) vs Proficient (80) -->

  <example category="closing_technique" score="30" tier="needs_improvement">
    <dialogue>
      <agent speaker="agent" timestamp="08:45">
        So yeah, let me know if you want to move forward
      </agent>
    </dialogue>
    <analysis>
      <technique_used>weak_next_steps</technique_used>
      <score>30</score>
      <reasoning>
        Passive closing ("let me know") puts burden on customer.
        No commitment requested, no specific next step.
      </reasoning>
    </analysis>
    <improvement>
      Should use direct ask or assumptive close:
      - "Based on the $10K/month savings, are you ready to move forward?"
      - "I'll get that contract sent over. Best email address?"
    </improvement>
  </example>

  <example category="closing_technique" score="80" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="08:45">
        Based on the $10K/month savings we discussed, are you ready to move forward?
      </agent>
      <customer speaker="customer" timestamp="08:48">Yes, let's do it</customer>
    </dialogue>
    <analysis>
      <technique_used>direct_ask_value</technique_used>
      <score>80</score>
      <reasoning>
        References specific value ($10K/month savings).
        Direct question requesting commitment.
        Confidence shown in asking for decision.
      </reasoning>
    </analysis>
  </example>

  <example category="closing_technique" score="100" tier="exceptional">
    <dialogue>
      <agent speaker="agent" timestamp="08:45">
        Great! I'll get that set up for you. What's the best email for the contract?
      </agent>
    </dialogue>
    <analysis>
      <technique_used>assumptive_close</technique_used>
      <score>100</score>
      <reasoning>
        Assumes sale is complete, moves to logistics.
        Confident, natural progression.
        No hesitation or room for objection.
      </reasoning>
    </analysis>
    <threshold_note>
      Assumptive close (100) vs direct ask (80):
      - 100: "I'll set this up" (assumes yes)
      - 80: "Are you ready?" (asks for yes)
      Both are proficient, assumptive shows highest confidence.
    </threshold_note>
  </example>

  <borderline_calibration score_A="60" score_B="80">
    <question>What separates Trial Close Only (60) from Direct Ask (80)?</question>
    <answer>
      - 80+ includes specific value proposition or business impact reference
      - 60 is generic ("How does that sound?", "What do you think?")
      - 80+ requests commitment/decision
      - 60 only tests temperature, doesn't ask for decision
      - 80+ shows confidence, 60 shows hesitation
    </answer>
  </borderline_calibration>

  <!-- Greeting/Intro: Developing (50) vs Proficient (75); inbound variant (67) -->

  <example category="greeting_intro" score="50" tier="developing">
    <dialogue>
      <agent speaker="agent" timestamp="00:05">
        Hi, this is John from Acme Solutions
      </agent>
    </dialogue>
    <analysis>
      <criteria>
        <personalization>0 (no customer name)</personalization>
        <company_identification>1 (identified company)</company_identification>
        <purpose_stated>0 (no reason for call)</purpose_stated>
        <rapport_building>1 (friendly greeting)</rapport_building>
      </criteria>
      <calculation>(0+1+0+1)/4 × 100 = 50</calculation>
    </analysis>
    <improvement>
      Missing personalization and call purpose.
      Better: "Hi Sarah, this is John from Acme Solutions. I'm calling about the reporting demo you requested."
    </improvement>
  </example>

  <example category="greeting_intro" score="75" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="00:05">
        Hi Sarah, this is John from Acme Solutions. Do you have a few minutes?
      </agent>
    </dialogue>
    <analysis>
      <criteria>
        <personalization>1 (used customer name)</personalization>
        <company_identification>1 (identified company)</company_identification>
        <purpose_stated>0 (no reason for call)</purpose_stated>
        <rapport_building>1 (asked permission for time)</rapport_building>
      </criteria>
      <calculation>(1+1+0+1)/4 × 100 = 75</calculation>
    </analysis>
    <threshold_note>
      Score 70+ means all applicable criteria are met, or with four applicable at
      most one is missing. Here the purpose is missing (outbound). Name unknown
      (inbound): 3 apply, and 2 of 3 is only 67.
    </threshold_note>
  </example>

  <example category="greeting_intro" score="67" tier="developing">
    <note>Inbound call (direction: inbound); the customer's name is not known.</note>
    <dialogue>
      <agent speaker="agent" timestamp="00:03">Acme Solutions.</agent>
      <customer speaker="customer" timestamp="00:05">Hi, I'd like to book the reporting demo.</customer>
      <agent speaker="agent" timestamp="00:09">The reporting demo, right. What day works for you?</agent>
    </dialogue>
    <analysis>
      <criteria>
        <personalization>not applicable (the customer's name is not known)</personalization>
        <company_identification>1 (identified company)</company_identification>
        <purpose_stated>1 (confirmed the customer's request)</purpose_stated>
        <rapport_building>0 (curt, no welcome)</rapport_building>
      </criteria>
      <calculation>(1+1+0)/3 × 100 = 67</calculation>
    </analysis>
    <improvement>
      A short welcome would complete it: "Acme Solutions, John speaking, how can I help?"
    </improvement>
  </example>

  <borderline_calibration score_A="50" score_B="75">
    <question>What separates Developing greeting (50) from Proficient (75)?</question>
    <answer>
      - 75+ is 3 of 4 or better (name unknown: all 3); 50 meets about half
    </answer>
  </borderline_calibration>

  <!-- Cross-cutting example: Multiple dimensions -->

  <comprehensive_example>
    <call_summary>
      Complete sales call showing multiple evaluation dimensions
    </call_summary>
    <dialogue>
      <agent timestamp="00:05">Hi Sarah, this is John from Acme Solutions. I'm calling about the reporting demo you requested. Do you have a few minutes?</agent>
      <customer timestamp="00:12">Yes, sure</customer>
      <agent timestamp="00:15">Great! Tell me about your current reporting challenges</agent>
      <customer timestamp="00:20">We're losing 10 hours per week on manual reports</customer>
      <agent timestamp="00:25">That's significant. What's your budget for solving this?</agent>
      <customer timestamp="00:30">Around $50K</customer>
      <agent timestamp="00:35">Perfect. Our solution typically saves 12 hours/week. When do you need this in place?</agent>
      <customer timestamp="00:42">By end of Q2</customer>
      <agent timestamp="00:45">Are you the decision maker, or is there a team involved?</agent>
      <customer timestamp="00:50">I need CFO approval</customer>
      <agent timestamp="00:55">That makes sense. Can we schedule a call with your CFO next week?</agent>
      <customer timestamp="01:00">Yes, let's do Tuesday</customer>
    </dialogue>
    <comprehensive_analysis>
      <greeting_intro>
        <score>100</score>
        <reasoning>All 4 elements present: name, company, purpose, permission: (1+1+1+1)/4 × 100 = 100</reasoning>
      </greeting_intro>
      <needs_qualification>
        <bant_score>88</bant_score>
        <reasoning>
          Budget: 2 (specific $50K)
          Authority: 1 (CFO approval identified)
          Need: 2 (quantified 10h/week)
          Timeline: 2 (specific Q2 end)
          Total: (2+1+2+2)/8 × 100 = 87.5, rounded to 88
        </reasoning>
      </needs_qualification>
      <closing>
        <score>85</score>
        <reasoning>
          Alternative choice close (Tuesday meeting).
          Proactive next step with decision maker.
          Not assumptive (would be 100) but strong commitment.
        </reasoning>
      </closing>
    </comprehensive_analysis>
  </comprehensive_example>

</examples>
`;
